import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';
import { v4 as uuidv4 } from 'uuid';
import {
  ArrowLeft, Upload, FileArchive, X, Link2, CheckCircle2,
  ChevronDown, ChevronUp, Plus, Zap, Bot, Image as ImageIcon, Puzzle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { getPendingZipFile, clearPendingZipFile } from '@/components/skills/DropSkillZone';
import type { SkillType, SkillTeam, SkillContextLink } from '../../../shared/types';

const TEAMS: SkillTeam[] = [
  'Business BR', 'Business SPLA', 'Business',
  'Compensations and Experience', 'Creative Studio', 'Data', 'Development',
  'Enterprise', 'Finance', 'Growth Partners', 'Growth', 'IT', 'Marketing',
  'New Solutions', 'Operations', 'Paid Media Lab', 'People', 'Performance',
  'Retail Media', 'Sales', 'Sharing', 'Strategy & Ops', 'Strategy & Planning',
  'Startup', 'Talent Acquisition', 'Tech & Product', 'Tech', 'US', 'We Care',
];

const SKILL_PROMPT = `Quiero crear una skill para el marketplace de IA de Winclap.

Ayudame a escribir el prompt y empaquetarlo en un ZIP listo para subir.

Mi idea: [describí qué debería hacer esta skill]

Necesito que el ZIP tenga:
- prompt.txt → el system prompt completo
- README.md → descripción corta (1-2 líneas)

Creá el prompt y generame el ZIP.`;

const AGENT_PROMPT = `Quiero crear un agente para el marketplace de IA de Winclap.

Ayudame a diseñar el agente y empaquetarlo en un ZIP listo para subir.

Mi idea: [describí qué debería hacer el agente, qué flujo sigue, qué herramientas necesita]

Necesito que el ZIP tenga:
- prompt.txt → las instrucciones completas del agente (rol, flujo, comportamiento)
- README.md → descripción corta (1-2 líneas) y cómo activarlo

Creá el agente y generame el ZIP.`;

const PLUGIN_PROMPT = `Quiero publicar un plugin para el marketplace de IA de Winclap.

Un plugin es un conjunto de skills/agentes que se cargan juntos desde terminal usando tu herramienta de IA.

Mi idea: [describí qué skills o agentes incluye el plugin y para qué equipo es]

Necesito que el ZIP tenga:
- prompt.txt → descripción general del plugin y sus capacidades
- README.md → instrucciones de instalación (cómo cargarlo con tu herramienta de IA)

Ayudame a prepararlo y generame el ZIP.`;

interface ParsedSkill {
  name: string;
  prompt: string;
  description: string;
}

async function parseZip(file: File): Promise<ParsedSkill> {
  const zip = await JSZip.loadAsync(file);
  const allFiles = Object.values(zip.files).filter((f) => !f.dir);

  // Sort by depth so root-level files are preferred
  const byDepth = allFiles.sort((a, b) => {
    const aDepth = (a.name.match(/\//g) || []).length;
    const bDepth = (b.name.match(/\//g) || []).length;
    return aDepth - bDepth;
  });

  const promptCandidates = ['prompt.txt', 'skill.md', 'agent.md', 'prompt.md'];
  let promptContent = '';
  let description = '';
  let name = file.name.replace(/\.zip$/i, '').replace(/[-_]/g, ' ');

  // Search all files by basename (case-insensitive), prefer shallower paths
  for (const candidate of promptCandidates) {
    const found = byDepth.find(
      (f) => f.name.split('/').pop()!.toLowerCase() === candidate.toLowerCase()
    );
    if (found) {
      const raw = await found.async('string');
      if (found.name.toLowerCase().endsWith('.md')) {
        const match = raw.match(/^---\n[\s\S]*?\n---\n?([\s\S]*)$/);
        promptContent = match ? match[1].trim() : raw.trim();
        const descMatch = raw.match(/description:\s*"?([^"\n]+)"?/);
        if (descMatch) description = descMatch[1].trim();
        const nameMatch = raw.match(/winclap-name:\s*"?([^"\n]+)"?/);
        if (nameMatch) name = nameMatch[1].trim();
      } else {
        promptContent = raw.trim();
      }
      break;
    }
  }

  // README for description — search all files by basename
  if (!description) {
    const readme = byDepth.find(
      (f) => f.name.split('/').pop()!.toLowerCase() === 'readme.md'
    );
    if (readme) {
      const raw = await readme.async('string');
      const firstLine = raw.split('\n').find((l) => l.trim() && !l.startsWith('#'));
      if (firstLine) description = firstLine.trim();
    }
  }

  return { name, prompt: promptContent, description };
}

export function NewSkillAgentPage() {
  const navigate = useNavigate();
  const { skillId: existingSkillId } = useParams<{ skillId?: string }>();
  const isAddVersion = !!existingSkillId;
  const utils = trpc.useUtils();
  const { t } = useTranslation();
  const skillZoneRef = useRef<HTMLInputElement>(null);
  const contextZipRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Load existing skill when adding a version
  const { data: existingSkill } = trpc.skills.getById.useQuery(
    { skillId: existingSkillId! },
    { enabled: isAddVersion }
  );

  // Type selection
  const [skillType, setSkillType] = useState<SkillType>('skill');

  // Step 1: ZIP
  const [isDragOver, setIsDragOver] = useState(false);
  const [parsed, setParsed] = useState<ParsedSkill | null>(null);
  const [skillFileName, setSkillFileName] = useState('');
  const [isParsing, setIsParsing] = useState(false);

  // Step 2: Core metadata
  const [name, setName] = useState('');
  const [team, setTeam] = useState<SkillTeam | ''>('');
  const [notes, setNotes] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);

  // Step 3: Context — multimedia
  const [contextImages, setContextImages] = useState<{ id: string; file: File; previewUrl: string; s3Key?: string }[]>([]);
  const [contextLinks, setContextLinks] = useState<SkillContextLink[]>([]);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [contextZipKey, setContextZipKey] = useState<string | undefined>();
  const [contextZipName, setContextZipName] = useState('');
  const [driveLink, setDriveLink] = useState('');
  const [isUploadingContext, setIsUploadingContext] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const getContextUploadUrlMutation = trpc.skills.getContextUploadUrl.useMutation();
  const getImageUploadUrlMutation = trpc.skills.getImageUploadUrl.useMutation();

  const handleSkillFile = useCallback(async (file: File) => {
    if (!file.name.endsWith('.zip') && !file.name.endsWith('.skill')) {
      toast.error(t('skills.form.el_archivo_debe_ser_un_zip'));
      return;
    }
    setIsParsing(true);
    try {
      const result = await parseZip(file);
      if (!result.prompt) {
        toast.error(t('skills.form.no_encontramos_un_prompt_en_el'));
        return;
      }
      setParsed(result);
      setName(result.name);
      setSkillFileName(file.name);
    } catch {
      toast.error(t('skills.form.no_se_pudo_leer_el_zip'));
    } finally {
      setIsParsing(false);
    }
  }, [t]);

  useEffect(() => {
    const pending = getPendingZipFile();
    if (pending) { clearPendingZipFile(); handleSkillFile(pending); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createMutation = trpc.skills.create.useMutation({
    onSuccess: () => {
      const label = skillType === 'agent' ? 'Agente' : skillType === 'plugin' ? 'Plugin' : 'Skill';
      toast.success(`${label} publicado ✓`);
      utils.skills.getAll.invalidate();
      navigate('/skills-agentes');
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const addVersionMutation = trpc.skills.addVersion.useMutation({
    onSuccess: () => {
      toast.success(t('skills.form.nueva_version_publicada'));
      utils.skills.getAll.invalidate();
      if (existingSkillId) utils.skills.getById.invalidate({ skillId: existingSkillId });
      navigate('/skills-agentes');
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const handleContextZipFile = async (file: File) => {
    if (!file.name.endsWith('.zip')) { toast.error(t('skills.form.debe_ser_un_zip')); return; }
    setIsUploadingContext(true);
    try {
      const { uploadUrl, s3Key } = await getContextUploadUrlMutation.mutateAsync({ skillId: uuidv4() });
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': 'application/zip' } });
      setContextZipKey(s3Key);
      setContextZipName(file.name);
      toast.success(t('skills.form.contexto_subido'));
    } catch { toast.error(t('skills.form.no_se_pudo_subir_el_zip')); }
    finally { setIsUploadingContext(false); }
  };

  const handleImageFiles = async (files: FileList | null) => {
    if (!files) return;
    const tempId = uuidv4();
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    for (const file of Array.from(files)) {
      if (!allowed.includes(file.type)) { toast.error(`${file.name} no es una imagen válida`); continue; }
      const id = uuidv4();
      const previewUrl = URL.createObjectURL(file);
      setContextImages((prev) => [...prev, { id, file, previewUrl }]);
      try {
        const ct = file.type as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif';
        const { uploadUrl, s3Key } = await getImageUploadUrlMutation.mutateAsync({ skillId: tempId, contentType: ct });
        await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': ct } });
        setContextImages((prev) => prev.map((img) => img.id === id ? { ...img, s3Key } : img));
      } catch { toast.error(`No se pudo subir ${file.name}`); setContextImages((prev) => prev.filter((i) => i.id !== id)); }
    }
  };

  const addLink = () => {
    if (!newLinkTitle.trim() || !newLinkUrl.trim()) return;
    setContextLinks((prev) => [...prev, { id: uuidv4(), title: newLinkTitle.trim(), url: newLinkUrl.trim() }]);
    setNewLinkTitle(''); setNewLinkUrl('');
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim() && !isAddVersion) e.name = t('skills.form.error_name');
    if (!team && !isAddVersion) e.team = t('skills.form.error_team');
    if (isAddVersion && !notes.trim()) e.notes = t('skills.form.error_notes');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!parsed || !validate()) return;

    if (isAddVersion && existingSkillId) {
      addVersionMutation.mutate({ skillId: existingSkillId, prompt: parsed.prompt, notes: notes.trim() });
      return;
    }

    const allLinks = [...contextLinks];
    if (driveLink.trim()) allLinks.push({ id: uuidv4(), title: t('skills.form.carpeta_de_contexto_google_drive'), url: driveLink.trim() });
    const coverImageKey = contextImages.find((i) => i.s3Key)?.s3Key;

    createMutation.mutate({
      name: name.trim() || parsed.name,
      type: skillType,
      team: team as SkillTeam,
      description: parsed.description || name.trim() || parsed.name,
      prompt: parsed.prompt,
      notes: '',
      contextZipKey,
      contextLinks: allLinks,
      coverImageKey,
    });
  };

  // ─── Step 0: Type selection + drop zone ────────────────────────────────────
  if (!parsed) {
    return (
      <div className="space-y-6 p-6 max-w-xl mx-auto">
        <div>
          <Button variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('skills.history.back')}
          </Button>
          <h2 className="text-2xl font-bold tracking-tight">{t('skills.form.upload_btn')}</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('skills.form.page_subtitle')}
          </p>
        </div>

        {/* Type selector */}
        <div className="grid grid-cols-3 gap-3">
          {([
            { value: 'skill', label: t('skills.card.type_skill'), icon: <Zap className="h-5 w-5" />, desc: t('skills.form.skill_type_desc') },
            { value: 'agent', label: t('skills.card.type_agent'), icon: <Bot className="h-5 w-5" />, desc: t('skills.form.agent_type_desc') },
            { value: 'plugin', label: t('skills.card.type_plugin'), icon: <Puzzle className="h-5 w-5" />, desc: t('skills.form.plugin_type_desc') },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSkillType(opt.value)}
              className={cn(
                'flex flex-col items-start gap-1.5 rounded-2xl border-2 p-4 text-left transition-all',
                skillType === opt.value
                  ? 'border-primary bg-primary/5'
                  : 'border-muted hover:border-muted-foreground/40'
              )}
            >
              <div className={cn('flex items-center gap-2 font-semibold', skillType === opt.value ? 'text-primary' : '')}>
                {opt.icon} {opt.label}
              </div>
              <p className="text-xs text-muted-foreground">{opt.desc}</p>
            </button>
          ))}
        </div>

        {/* ZIP drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={async (e) => { e.preventDefault(); setIsDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleSkillFile(f); }}
          onClick={() => skillZoneRef.current?.click()}
          className={cn(
            'flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-14 cursor-pointer transition-all',
            isParsing ? 'border-primary bg-primary/5 cursor-wait'
              : isDragOver ? 'border-primary bg-primary/5 scale-[1.01]'
              : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/40'
          )}
        >
          {isParsing ? (
            <><div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" /><p className="text-sm text-muted-foreground font-medium">{t('skills.form.dropzone_parsing')}</p></>
          ) : (
            <><FileArchive className={cn('h-12 w-12', isDragOver ? 'text-primary' : 'text-muted-foreground/40')} /><div className="text-center"><p className="font-semibold">{t('skills.dropzone.title')}</p><p className="text-sm text-muted-foreground mt-0.5">{t('skills.form.dropzone_click')}</p></div></>
          )}
          <input ref={skillZoneRef} type="file" accept=".zip,.skill" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSkillFile(f); }} />
        </div>

        {/* Prompt template */}
        <Card className="p-4 space-y-3 bg-muted/30 border-dashed">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('skills.form.no_zip_title')}</p>
          <p className="text-sm text-muted-foreground">{t('skills.form.no_zip_desc')}</p>
          <pre className="text-xs bg-background rounded-lg p-3 border whitespace-pre-wrap font-mono leading-relaxed">
            {skillType === 'agent' ? AGENT_PROMPT : skillType === 'plugin' ? PLUGIN_PROMPT : SKILL_PROMPT}
          </pre>
          <Button variant="outline" size="sm" className="w-full" onClick={async (e) => {
            e.stopPropagation();
            const prompt = skillType === 'agent' ? AGENT_PROMPT : skillType === 'plugin' ? PLUGIN_PROMPT : SKILL_PROMPT;
            await navigator.clipboard.writeText(prompt);
            toast.success(t('skills.form.copy_prompt_toast'));
          }}>
            {t('skills.guide.copy_prompt_btn')}
          </Button>
        </Card>
      </div>
    );
  }

  // ─── Step 1: Review + team + context ───────────────────────────────────────
  const isPending = isAddVersion ? addVersionMutation.isPending : createMutation.isPending;

  return (
    <div className="space-y-5 p-6 max-w-xl mx-auto">
      <div>
        <Button variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground" onClick={() => { setParsed(null); setName(''); setTeam(''); setNotes(''); }}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('skills.history.back')}
        </Button>
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold tracking-tight">
            {isAddVersion ? t('skills.form.title_version') : t('skills.form.review_title')}
          </h2>
          <Badge variant="outline" className="capitalize">{skillType === 'agent' ? t('skills.card.type_agent') : skillType === 'plugin' ? t('skills.card.type_plugin') : t('skills.card.type_skill')}</Badge>
        </div>
        <p className="text-muted-foreground mt-1 text-sm">
          {isAddVersion
            ? t('skills.form.updating_version', { name: existingSkill?.name ?? '...', version: (existingSkill?.currentVersion ?? 0) + 1 })
            : t('skills.form.review_subtitle')
          }
        </p>
      </div>

      {/* Core info */}
      <Card className="p-4 space-y-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
          <span className="font-mono truncate">{skillFileName}</span>
        </div>

        {isAddVersion ? (
          /* Add-version mode: show notes field, hide team/name */
          <div className="space-y-1.5">
            <Label htmlFor="notes" className="text-sm font-semibold">
              {t('skills.form.version_notes_label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="notes"
              value={notes}
              onChange={(e) => { setNotes(e.target.value); setErrors((p) => ({ ...p, notes: '' })); }}
              placeholder={t('skills.form.version_notes_placeholder')}
              className={cn(errors.notes && 'border-destructive')}
            />
            {errors.notes && <p className="text-xs text-destructive font-medium">{errors.notes}</p>}
          </div>
        ) : (
          <>
            {/* TEAM — first, most prominent */}
            <div className="space-y-1.5">
              <Label htmlFor="team" className="text-sm font-semibold">
                {t('skills.form.team_question')} <span className="text-destructive">*</span>
              </Label>
              <Select value={team} onValueChange={(v) => { setTeam(v as SkillTeam); setErrors({}); }}>
                <SelectTrigger id="team" className={cn('w-full', errors.team && 'border-destructive')}>
                  <SelectValue placeholder={t('skills.form.team_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {TEAMS.map((teamName) => <SelectItem key={teamName} value={teamName}>{teamName}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.team && <p className="text-xs text-destructive font-medium">{errors.team}</p>}
            </div>

            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-sm font-semibold">
                Nombre <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: '' })); }}
                placeholder={t('skills.form.ej_redactor_de_emails_en_frio')}
                className={cn(errors.name && 'border-destructive')}
              />
              {errors.name
                ? <p className="text-xs text-destructive font-medium">{errors.name}</p>
                : <p className="text-xs text-muted-foreground">{t('skills.form.name_hint')}</p>
              }
            </div>
          </>
        )}

        {/* Prompt preview */}
        <div>
          <button type="button" onClick={() => setShowPrompt((v) => !v)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            {showPrompt ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {showPrompt ? t('skills.form.hide_prompt') : t('skills.form.show_prompt')}
          </button>
          {showPrompt && (
            <pre className="mt-2 whitespace-pre-wrap font-mono text-xs bg-muted/40 rounded-lg p-3 border max-h-44 overflow-y-auto">
              {parsed.prompt}
            </pre>
          )}
        </div>
      </Card>

      {/* Contexto multimedia — solo para skills nuevas */}
      {!isAddVersion && <div className="space-y-3">
        <div>
          <p className="text-sm font-semibold">{t('skills.form.context_title')} <span className="text-muted-foreground font-normal text-xs">{t('skills.form.context_optional')}</span></p>
          <p className="text-xs text-muted-foreground mt-0.5">{t('skills.form.context_desc', { type: skillType === 'agent' ? t('skills.card.type_agent').toLowerCase() : t('skills.card.type_skill').toLowerCase() })}</p>
        </div>

        {/* Images drop zone */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleImageFiles(e.dataTransfer.files); }}
          onClick={() => imageInputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-5 cursor-pointer text-sm text-muted-foreground hover:border-primary/50 hover:bg-accent/40 transition-colors"
        >
          <ImageIcon className="h-5 w-5" />
          <p>{t('skills.form.images_dropzone')}</p>
          <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={(e) => handleImageFiles(e.target.files)} />
        </div>

        {/* Image previews */}
        {contextImages.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {contextImages.map((img) => (
              <div key={img.id} className="relative group">
                <img src={img.previewUrl} alt="" className="h-16 w-16 rounded-lg object-cover border" />
                {!img.s3Key && <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40"><div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" /></div>}
                <button onClick={() => setContextImages((p) => p.filter((i) => i.id !== img.id))} className="absolute -top-1.5 -right-1.5 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Links */}
        <div className="space-y-2">
          {contextLinks.length > 0 && (
            <ul className="space-y-1.5">
              {contextLinks.map((link) => (
                <li key={link.id} className="flex items-center gap-2 text-sm bg-muted/40 rounded-md px-3 py-1.5">
                  <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="font-medium flex-1 truncate text-xs">{link.title}</span>
                  <span className="text-xs text-muted-foreground truncate max-w-[140px]">{link.url}</span>
                  <button onClick={() => setContextLinks((p) => p.filter((l) => l.id !== link.id))}>
                    <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <Input placeholder={t('skills.form.titulo_del_link')} value={newLinkTitle} onChange={(e) => setNewLinkTitle(e.target.value)} className="flex-1 text-sm" />
            <Input placeholder="https://..." value={newLinkUrl} onChange={(e) => setNewLinkUrl(e.target.value)} className="flex-1 text-sm" onKeyDown={(e) => e.key === 'Enter' && addLink()} />
            <Button type="button" variant="outline" size="icon" aria-label={t('common.a11y.add')} onClick={addLink} disabled={!newLinkTitle.trim() || !newLinkUrl.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Google Drive link */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
            <Link2 className="h-3.5 w-3.5" /> {t('skills.form.drive_label')} <span className="font-normal">{t('skills.form.drive_public')}</span>
          </div>
          <Input
            placeholder={t('skills.form.drive_placeholder')}
            value={driveLink}
            onChange={(e) => setDriveLink(e.target.value)}
            className="text-sm"
          />
          <p className="text-xs text-muted-foreground">{t('skills.form.drive_hint')}</p>
        </div>

        {/* Context ZIP */}
        <div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium mb-1.5">
            <FileArchive className="h-3.5 w-3.5" /> {t('skills.form.context_zip_label')}
          </div>
          {contextZipKey ? (
            <div className="flex items-center gap-2 rounded-lg border bg-green-50 px-3 py-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
              <span className="flex-1 font-mono text-xs text-green-800 truncate">{contextZipName}</span>
              <button onClick={() => { setContextZipKey(undefined); setContextZipName(''); }}>
                <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
              </button>
            </div>
          ) : (
            <div onClick={() => contextZipRef.current?.click()} className={cn('flex items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 cursor-pointer text-sm text-muted-foreground hover:border-primary/50 transition-colors', isUploadingContext && 'cursor-wait')}>
              {isUploadingContext ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" /> : <Upload className="h-4 w-4" />}
              {isUploadingContext ? t('skills.form.context_zip_uploading') : t('skills.form.context_zip_dropzone')}
            </div>
          )}
          <input ref={contextZipRef} type="file" accept=".zip" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleContextZipFile(f); }} />
        </div>
      </div>}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2 border-t">
        <Button variant="outline" onClick={() => navigate(-1)} disabled={isPending}>{t('skills.form.cancel_btn')}</Button>
        <Button
          onClick={handleSubmit}
          disabled={isPending || (!isAddVersion && (!team || !name.trim())) || (isAddVersion && !notes.trim())}
        >
          {isPending
            ? t('skills.form.publishing')
            : isAddVersion
            ? t('skills.form.publish_version_btn')
            : t('skills.form.publish_btn')
          }
        </Button>
      </div>
    </div>
  );
}
